/*************************************************
 * MaxCap
 * @exports
 * AuthApi.ts
 * Created by vinoth kumar on  15/05/2024
 * Copyright © 2024 MaxCap. All rights reserved.
 *************************************************/

import { createApi } from "@reduxjs/toolkit/query/react";
import CustomFetchBase from "./CustomFetchBase";
import { setUser, setImpersonateUser } from "../slice/UserSlice";

export const authApi = createApi({
    reducerPath: "authApi",
    baseQuery: CustomFetchBase,
    endpoints: (builder) => ({
        loginUser: builder.mutation({
            query: (body) => ({
                url: `login`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (args, {dispatch, queryFulfilled}) => {
                try {
                    const {data} = await queryFulfilled;
                    if (data.data?.users?.is_verified) {
                        //dispatch(setUser([]));
                        dispatch(setUser(data.data?.users));
                    }
                } catch (error) {
                }
            },
        }),
        googleAuth: builder.mutation({
            query: (body) => ({
                url: `google-auth`,
                method: "POST",
                body,
            }),
        }),
        logoutUser: builder.mutation({
            query: (body) => ({
                url: `logout`,
                method: "POST",
                body,
            }),
        }),
        forgotPassword: builder.mutation({
            query: (body) => ({
                url: `send-forgot-password-otp`,
                method: "POST",
                body,
            }),
        }),
        changePassword: builder.mutation({
            query: (body) => ({
                url: `rest-password`,
                method: "POST",
                body,
            }),
        }),
        registerUser: builder.mutation({
            query: (body) => ({
                url: `register`,
                method: "POST",
                body,
            }),
        }),
        lookupUser: builder.mutation({
            query: (body) => ({
                url: `lookup-user`,
                method: "POST",
                body,
            }),
        }),
        verifyEmail: builder.mutation({
            query: (body) => ({
                url: `verify-email`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (args, {dispatch, queryFulfilled}) => {
                try {
                    const {data} = await queryFulfilled;
                    if (data.data?.users?.is_verified) {
                        dispatch(setUser(data.data?.users));
                    }
                } catch (error) {
                }
            },
        }),
        rensendVerifyEmail: builder.mutation({
            query: (body) => ({
                url: `resend-verify-email`,
                method: "POST",
                body,
            }),
        }),
        getDashboardDetails: builder.query({
            query: () => ({
                url: `dashboard`,
                method: "GET",
            }),
        }),
        requestPayoutLater: builder.mutation({
            query: (body) => ({
                url: `request-payoff-letter`,
                method: "POST",
                body,
            }),
        }),
        // viewStatement: builder.mutation({
        //   query: (body) => ({
        //     url: `download-statement`,
        //     method: "POST",
        //     body,
        //   }),
        // }),
        viewStatement: builder.mutation<
            Blob,
            {
                opportunityNumber: string;
                startDate: string;
                endDate: string;
            }
        >({
            query: (body) => ({
                url: "download-statement",
                method: "POST",
                body,
                headers: {
                    "Content-Type": "application/json",
                },
                responseHandler: (response: Response) => response.blob(),
                cache: "no-store",
            }),
        }),
        emailStatement: builder.mutation<
            {
                success: boolean;
                delivered?: boolean;
                to?: string;
                file?: string;
                message?: string;
            },
            {
                opportunityNumber: string;
                startDate: string;
                endDate: string;
                businessName?: string;
            }
        >({
            query: (body) => ({
                url: "email-statement",
                method: "POST",
                body,
            }),
        }),
        emailPaymentReceipt: builder.mutation<
            {
                success: boolean;
                delivered?: boolean;
                to?: string;
                receipt?: string;
                message?: string;
            },
            {
                opportunityNumber: string;
                businessName?: string;
                paymentNumber?: number | string;
                date: string;
                amount: number;
                balance?: number;
                status?: string;
                description?: string;
                receiptRef?: string;
            }
        >({
            query: (body) => ({
                url: "email-payment-receipt",
                method: "POST",
                body,
            }),
        }),
        emailPaymentSchedule: builder.mutation<
            {
                success: boolean;
                delivered?: boolean;
                to?: string;
                file?: string;
                message?: string;
            },
            {
                opportunityNumber: string;
                businessName?: string;
                frequency?: string;
                rows: Array<{
                    date: string;
                    amount: number;
                    balance?: number | null;
                    status: string;
                    moved?: boolean;
                }>;
            }
        >({
            query: (body) => ({
                url: "email-payment-schedule",
                method: "POST",
                body,
            }),
        }),
        requestFunding: builder.mutation({
            query: (body) => ({
                url: `request-funding`,
                method: "POST",
                body,
            }),
        }),
        referFriend: builder.mutation({
            query: (body) => ({
                url: `refer-friend`,
                method: "POST",
                body,
            }),
        }),
        getImpersonateUser: builder.query({
            query: (params) => ({
                url: `impersonate-signin`,
                method: "GET",
                params,
            }),
            onQueryStarted: async (args, {dispatch, queryFulfilled}) => {
                try {
                    const {data} = await queryFulfilled;
                    if (data.data?.users?.is_verified) {
                        dispatch(setUser(data.data?.users));
                        dispatch(setImpersonateUser());

                    }
                } catch (error) {
                }
            },
        }),
        lazyGetAppFormDetails: builder.mutation({
            query: (body) => ({
                url: `get-application-form-data`,
                method: "POST",
                body,
            }),
        }),
        submitAppForm: builder.mutation({
            query: (body) => ({
                url: `submit-application-form`,
                method: "POST",
                body,
            }),
        }),
        updateRenewalMailingAddress: builder.mutation({
            query: (body) => ({
                url: `update-renewal-mailing-address`,
                method: "POST",
                body,
            }),
        }),
        getCanSubmitNewApp: builder.mutation({
            query: () => ({
                url: `can-submit-new-application`,
                method: "POST",
            }),
        }),
        documentsUpload: builder.mutation({
            query: (body) => ({
                url: `file-upload`,
                method: "POST",
                body,
            }),
        }),
        getUsersList: builder.query({
            query: ({currentPage, searchQuery}) => {
                return {
                    url: `users?page=${currentPage}${searchQuery}`,
                    method: "GET",
                };
            },
        }),
        getUserDetails: builder.query({
            query: ({userId}) => ({
                url: `users/${userId}`,
                method: "GET",
            }),
        }),
        updateUser: builder.mutation({
            query: (args) => {
                const {userId, body} = args;
                return {
                    url: `/users/${userId}`,
                    method: "PUT",
                    body,
                };
            }
        }),
        activateUser: builder.mutation({
            query: ({userId}) => ({
                url: `user/${userId}/activate`,
                method: "POST",
            }),
        }),
        deactivateUser: builder.mutation({
            query: ({userId}) => ({
                url: `user/${userId}/deactivate`,
                method: "POST",
            }),
        }),
        deleteUser: builder.mutation({
            query: ({userId}) => ({
                url: `users/${userId}`,
                method: "DELETE",
            }),
        }),
        getUsersDataCSV: builder.query({
            query: () => ({
                url: `/users/export`,
                method: "GET",
            }),
        }),
        sendPlaidJourney: builder.mutation({
            query: (body) => {
                return {
                    url: `plaid/journey`,
                    method: "POST",
                    body,
                };
            }
        }),
        sendPlaidExchange: builder.mutation({
            query: (body) => {
                return {
                    url: `plaid/exchange`,
                    method: "POST",
                    body,
                };
            }
        }),
        getOrderDetails: builder.query({
            query: (opportunityNumber: string) => ({
                url: `order/${opportunityNumber}`,
                method: "GET",
            }),
        }),
        downloadLoanAgreement: builder.mutation({
            query: (body) => ({
                url: `download-loan-agreement`,
                method: "POST",
                body,
                headers: {
                    "Content-Type": "application/json",
                },
                responseHandler: async (response: Response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const blob = await response.blob();
                    const arrayBuffer = await blob.arrayBuffer();
                    const uint8Array = new Uint8Array(arrayBuffer);

                    const pdfSignature = [0x25, 0x50, 0x44, 0x46];
                    const isValidPDF = pdfSignature.every((byte, index) => uint8Array[index] === byte);

                    if (!isValidPDF) {
                        const text = new TextDecoder().decode(uint8Array);
                        console.log('Invalid PDF response:', text);
                        throw new Error('Document not found or server returned an error page');
                    }

                    return blob;
                },
            }),
        }),
        sendRenewalEmail: builder.mutation({
            query: () => {
                return {
                    url: `send-email-for-renewal`,
                    method: "POST",
                };
            }
        }),
        sendRenewalSummary: builder.mutation({
            query: (body: { opportunity_number?: string; started_at?: string }) => ({
                url: `send-renewal-summary`,
                method: "POST",
                body,
            }),
        }),
        notifyRmIndicativeOffers: builder.mutation({
            query: (body: {
                owner_email: string;
                owner_first_name?: string;
                customer_name: string;
                company_name?: string;
                opportunity_number: string;
                opportunity_id?: string;
                opportunity_link?: string;
                presented_on?: string;
                offers_valid_until?: string;
                offers: Array<{
                    name: string;
                    loan_amount: string;
                    payment_amount: string;
                    payment_label?: string;
                    total_repayable: string;
                    net_cash: string;
                }>;
            }) => ({
                url: `notify-rm-indicative-offers`,
                method: "POST",
                body,
            }),
        }),
        getRenewalOffers: builder.query<
            {
                success: boolean;
                opportunityId?: string;
                offers?: Array<{
                    termMonths: number | null;
                    termWeeks: number | null;
                    loanAmount: number | null;
                    paymentAmount: number | null;
                    paymentCount: number | null;
                    frequency: "weekly" | "monthly";
                    settlement: number | null;
                    originationFee: number | null;
                    totalRepayable: number | null;
                    netCash: number | null;
                    riskGrade?: string | null;
                }>;
                message?: string;
            },
            string
        >({
            query: (opportunityId) => ({
                url: `renewal-offers/${encodeURIComponent(opportunityId)}`,
                method: "GET",
            }),
        }),
        sendSupportRequest: builder.mutation({
            query: (body: {
                topic: string;
                channel?: "support" | "message" | "settlement";
                details?: string;
                specialist_email?: string;
                specialist_name?: string;
                account_number?: string;
                company_name?: string;
                customer_phone?: string;
                paid_pct?: number;
            }) => ({
                url: `send-support-request`,
                method: "POST",
                body,
            }),
        }),
        sendMeetingInvite: builder.mutation({
            query: (body: {
                specialist_email: string;
                specialist_name?: string;
                customer_name?: string;
                customer_email?: string;
                customer_phone?: string;
                company_name?: string;
                account_number?: string;
                topic?: string;
                slot?: string;
                date?: string;
                time?: string;
            }) => ({
                url: `send-meeting-invite`,
                method: "POST",
                body,
            }),
        }),
        sendRequestSettlement: builder.mutation({
            query: (formData: FormData) => ({
                url: `/request-settlement`,
                method: "POST",
                body: formData,
            }),
        }),
    })
})

export const {
  useLoginUserMutation,
  useGoogleAuthMutation,
  useLogoutUserMutation,
  useForgotPasswordMutation,
  useChangePasswordMutation,
  useRegisterUserMutation,
  useLookupUserMutation,
  useVerifyEmailMutation,
  useRensendVerifyEmailMutation,
  useLazyGetDashboardDetailsQuery,
  useViewStatementMutation,
  useEmailStatementMutation,
  useEmailPaymentReceiptMutation,
  useEmailPaymentScheduleMutation,
  useRequestPayoutLaterMutation,
  useRequestFundingMutation,
  useReferFriendMutation,
  useLazyGetImpersonateUserQuery,
  useLazyGetAppFormDetailsMutation,
  useSubmitAppFormMutation,
  useUpdateRenewalMailingAddressMutation,
  useGetCanSubmitNewAppMutation,
  useDocumentsUploadMutation,
  useLazyGetUsersListQuery,
  useLazyGetUserDetailsQuery,
  useUpdateUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useLazyGetUsersDataCSVQuery,
  useSendPlaidJourneyMutation,
  useSendPlaidExchangeMutation,
  useDownloadLoanAgreementMutation,
  useSendRenewalEmailMutation,
  useSendRenewalSummaryMutation,
  useNotifyRmIndicativeOffersMutation,
  useGetRenewalOffersQuery,
  useSendSupportRequestMutation,
  useSendMeetingInviteMutation,
  useSendRequestSettlementMutation,
  useLazyGetOrderDetailsQuery,
  useGetOrderDetailsQuery,
} = authApi;
